import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ButtonV2, FormControl, TextInput } from '@arc-ui/components';
import { EeIconSearch } from '@arc-ui/icons';
import { useSearchBox, useMenu } from 'react-instantsearch';
import { ARIA_LABELS } from '../../../constants';
import { useAccessibility } from '../../../hooks';

export const ArcSearchInput: React.FC = () => {
  const { query, refine } = useSearchBox();
  const { items: facetBuckets, refine: refineFacet } = useMenu({
    attribute: 'category',
  });
  const [value, setValue] = useState(query);
  const [hasSearched, setHasSearched] = useState(!!query.trim());
  const { announce } = useAccessibility();
  const prevQueryRef = useRef<string>(query);

  const handleInputChange = useCallback((newQuery: string) => {
    setValue(newQuery);
  }, []);

  const submitSearch = useCallback(() => {
    const trimmedNew = value.trim();
    if (!trimmedNew) return;

    const trimmedPrev = prevQueryRef.current.trim();

    if (trimmedNew !== trimmedPrev) {
      for (const facet of facetBuckets.filter((b) => b.isRefined)) {
        refineFacet(facet.value);
      }
    }

    refine(value);
    prevQueryRef.current = value;
    setHasSearched(true);
    announce('Search submitted');
  }, [value, refine, refineFacet, facetBuckets, announce]);

  const resetSearch = (): void => {
    setValue('');
    setHasSearched(false);
    announce('Search cleared');
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        submitSearch();
      }
    },
    [submitSearch],
  );

  useEffect(() => {
    setValue(query);
    if (query.trim()) {
      setHasSearched(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return (
    <div className="ee-searchBoxWrap" onKeyDown={handleKeyDown} role="search">
      <FormControl label={ARIA_LABELS.SEARCH_INPUT} hideLabel>
        <TextInput
          id="ee-search"
          label={ARIA_LABELS.SEARCH_INPUT}
          hideLabel
          isRequired
          size="m"
          value={value}
          onChange={(event) => handleInputChange(event.currentTarget.value)}
          clearButton={hasSearched ? {
            onClick: resetSearch,
            tooltipLabel: 'Clear search',
          } : undefined}
          trailingSlot={!hasSearched ? (
            <ButtonV2
              buttonStyle="compact"
              onClick={!value.trim() ? undefined : submitSearch}
              ariaLabel={ARIA_LABELS.SEARCH_BUTTON}
              icon={EeIconSearch}
              size='s'
            />
          ) : undefined}
          autoComplete="off"
        />
      </FormControl>
    </div>
  );
};
